import {
    Box, Button, Icon, Modal, ModalBody, ModalCloseButton,
    ModalContent, ModalFooter, ModalHeader, ModalOverlay,
    Table, Tbody, Td, Th, Thead, Tr, Text, useDisclosure, useToast, Heading
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Draggable from 'react-draggable';
import { useTranslation } from "react-i18next";
// API IMPORTS
import { useSession } from '../../SessionContext';
import { react_api } from "../../config";
import { HTTP_STATUS_CODES } from "../../GrinsCostant";
import logout from "../../services/authService";
import createAxiosInstance from "../../interceptors/useAxiosInterceptors";
import { FaUserMinus } from "react-icons/fa";  // Changed to user minus icon for deleted users

const DeletedUserList = () => {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });

    const navigate = useNavigate();
    const modalRef = useRef(null);

    // API - STATE HOOKS
    const { t } = useTranslation();
    const toast = useToast();
    const [message, setMessage] = useState("");
    const { updateSessionData, clearSessionData } = useSession();

    const axiosInstance = createAxiosInstance(async (axiosInstance) => {
        logout(t, clearSessionData, navigate, toast, axiosInstance)
    });

    const {
        isOpen: isModalOpen,
        onOpen: openModal,
        onClose: closeModal
    } = useDisclosure();

    // API REQUEST
    const fetchDeletedUsers = async () => {
        setLoading(true);

        try {
            const richiesta = {
                BearerToken: sessionStorage.getItem("token"),
                username: sessionStorage.getItem("username"),
            };

            const options = {
                headers: {
                    'Content-Type': 'application/json',
                },
            };

            const urlFetch = `${react_api}/admin/getListAccessRequest`;  // Changed endpoint for deleted users
            // const urlFetch = `${react_api}/admin/getListDeletedUsers`;  // Changed endpoint for deleted users
            const response = await axiosInstance.post(urlFetch, richiesta, options);
            const result = response.data;

            if (result.status === HTTP_STATUS_CODES.BAD_REQUEST || result.status === HTTP_STATUS_CODES.INTERNAL_SERVER_ERROR) {
                toast({
                    title: t('toastMsg.error'),
                    description: t(`messageBE.${result.message}`),
                    position: "top",
                    status: 'error',
                    duration: 4000,
                    isClosable: true,
                });
                setUsers([]);
            } else if (result.status === HTTP_STATUS_CODES.OK) {
                const userData = Array.isArray(result.data) ? result.data : [];
                setUsers(userData);
            } else {
                setMessage(result.message);
                setUsers([]);
            }

        } catch (error) {
            console.error('Error fetching deleted users:', error);
            toast({
                title: t('toastMsg.callError'),
                description: String(error),
                position: "top",
                status: 'warning',
                duration: 4000,
                isClosable: true,
            });
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    // Auto-load users on component mount
    useEffect(() => {
        fetchDeletedUsers();
    }, []);

    const handleDrag = (e, data) => {
        if (!modalRef.current) return;
        const { x, y } = data;
        const modalRect = modalRef.current.getBoundingClientRect();
        const modalWidth = modalRect.width;
        const modalHeight = modalRect.height;
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;

        const minVisibleRight = modalWidth * 0.5;
        const minVisibleLeft = modalWidth * 0.4;
        const minVisibleBottom = modalHeight * 1.0;
        const minVisibleTop = modalHeight * 1.0;
        let boundedX = x;
        let boundedY = y;

        if (x < -(modalWidth - minVisibleLeft)) boundedX = -(modalWidth - minVisibleLeft);
        if (x > windowWidth - minVisibleRight) boundedX = windowWidth - minVisibleRight;
        if (y < -(modalHeight - minVisibleTop)) boundedY = -(modalHeight - minVisibleTop);
        if (y > windowHeight - minVisibleBottom) boundedY = windowHeight - minVisibleBottom;

        setPosition({ x: boundedX, y: boundedY });
    };

    const handleUserAction = (user) => {
        setSelectedUser(user);
        const modalWidth = 600;
        const modalHeight = 300;
        const centerX = (window.innerWidth - modalWidth) / 2;
        const centerY = (window.innerHeight - modalHeight) / 2;
        setPosition({ x: centerX, y: centerY });
        openModal();
    };

    const modalHeaderColor = "#0888be";
    const sectionHeaderColor = "#0888be";
    // const modalHeaderColor = "#E53E3E";  // Color change for deleted users
    // const sectionHeaderColor = "#E53E3E";  // Color change for deleted users
    const rowBgColor = "gray.100";

    return (
        <Box h="100%" w="100%">
            <Heading size='md' color={"rgba(11,78,90,255)"}>Deleted Users List</Heading>
            <Box overflowY="auto" direction="column" h="260px" boxShadow="lg" borderRadius={"10px"}>
                <Table size="sm" variant='striped' bgcolor="white">
                    <Thead fontFamily={"-moz-initial"} bg={modalHeaderColor} position="sticky" top="0" zIndex={1}>
                        <Tr>
                            <Th color={"white"} width="20%">Name</Th>
                            <Th color={"white"} width="20%">Surname</Th>
                            <Th color={"white"} width="20%">Role</Th>
                            <Th color={"white"} width="15%">Organization</Th>
                            <Th color={"white"} width="20%">Deletion Date</Th>
                            <Th color={"white"} width="5%">Action</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {users.length > 0 ? (
                            users.map((user, index) => (
                                <Tr key={user.id || index}>
                                    <Td whiteSpace={"normal"} wordBreak={"break-word"}>{user.name || 'N/A'}</Td>
                                    <Td whiteSpace={"normal"} wordBreak={"break-word"}>{user.surname || 'N/A'}</Td>
                                    <Td whiteSpace={"normal"} wordBreak={"break-word"}>{user.role || 'N/A'}</Td>
                                    <Td whiteSpace={"normal"} wordBreak={"break-word"}>{user.organization || 'N/A'}</Td>
                                    <Td whiteSpace={"normal"} wordBreak={"break-word"}>
                                        {user.deletion_date
                                            ? user.deletion_date.substring(0, user.deletion_date.indexOf('.'))
                                            : 'N/A'
                                        }
                                    </Td>
                                    <Td>
                                        <Button size="sm" colorScheme="red" onClick={() => handleUserAction(user)}>
                                            <FaUserMinus />
                                        </Button>
                                    </Td>
                                </Tr>
                            ))
                        ) : (
                            <Tr alignContent={"center"}>
                                <Td colSpan={6}>
                                    <Text fontSize={"lg"} color="rgba(11,78,90,255)">
                                        {loading ? 'Loading...' : (message ? t(`messageBE.${message}`) : 'No deleted users found')}
                                    </Text>
                                </Td>
                            </Tr>
                        )}
                    </Tbody>
                </Table>
            </Box>

            {selectedUser && (
                <Modal
                    isOpen={isModalOpen}
                    onClose={closeModal}
                    size="xl"
                    scrollBehavior="inside"
                    closeOnOverlayClick={false}
                    motionPreset="none"
                    isCentered={false}
                >
                    <ModalOverlay />
                    <Draggable
                        handle=".modal-header"
                        position={position}
                        onDrag={handleDrag}
                        onStop={handleDrag}
                    >
                        <ModalContent
                            ref={modalRef}
                            maxW="600px"
                            minH="300px"
                            position="absolute"
                            top="0"
                            left="0"
                            m={0}
                            zIndex={1500}
                            overflow="hidden"
                            boxShadow="xl"
                        >
                            <ModalHeader
                                className="modal-header"
                                bg={modalHeaderColor}
                                color="white"
                                display="flex"
                                alignItems="center"
                                gap={2}
                                cursor="move"
                                px={4}
                                py={3}
                                fontSize="lg"
                            >
                                <FaUserMinus />
                                Deleted User Details
                            </ModalHeader>
                            <Box>
                                <ModalCloseButton color="white" mt={"1px"} />
                            </Box>
                            <ModalBody
                                tabIndex={0}
                                px={4}
                                py={2}
                                overflowY="auto"
                                maxH="calc(60vh - 120px)"
                                onWheel={(e) => e.stopPropagation()}
                            >
                                <Table size="sm" variant="simple" border Width="1px" borderColor="gray.200" boxShadow="sm">
                                    <Tbody>
                                        <Tr bg={rowBgColor}><Td fontWeight="bold">Name:</Td><Td>{selectedUser.name || 'N/A'}</Td></Tr>
                                        <Tr><Td fontWeight="bold">Surname:</Td><Td>{selectedUser.surname || 'N/A'}</Td></Tr>
                                        <Tr bg={rowBgColor}><Td fontWeight="bold">Email:</Td><Td>{selectedUser.email || 'N/A'}</Td></Tr>
                                        <Tr><Td fontWeight="bold">Role:</Td><Td>{selectedUser.role || 'N/A'}</Td></Tr>
                                        <Tr bg={rowBgColor}><Td fontWeight="bold">Organization:</Td><Td>{selectedUser.organization || 'N/A'}</Td></Tr>
                                        <Tr><Td fontWeight="bold">Deletion Date:</Td><Td>{selectedUser.deletion_date?.substring(0, selectedUser.deletion_date.indexOf('.')) || 'N/A'}</Td></Tr>
                                    </Tbody>
                                </Table>
                            </ModalBody>
                            <ModalFooter px={4} py={3}>
                                <Button colorScheme="red" onClick={closeModal}>Close</Button>
                            </ModalFooter>
                        </ModalContent>
                    </Draggable>
                </Modal>
            )}
        </Box>
    );
};

export default DeletedUserList;
